import { forwardRef } from 'react';
import { View } from './View';
import { ViewProps } from './ViewProps';
import styles from './ScrollView.module.css';

/**
 * ScrollView
 *
 * A web-native scrollable container, replacing react-native's ScrollView.
 * Scrolls vertically by default; pass `horizontal` for a row that scrolls
 * horizontally. Pass `showScrollIndicators` to display fade masks with arrows
 * at the scrollable edges when the content overflows.
 *
 * Sizing defaults to react-native-web ScrollView's `flex: 1 1 auto`: fill a
 * sized pane, size to content in a content-sized parent, shrink when
 * constrained. Override it via className, `flex`/`fill`, or `style` — the
 * default is declared at zero specificity, so any caller styling wins. The
 * ScrollView only scrolls when something (the flex default, a class, or a
 * sized parent) actually constrains it.
 *
 * The ref points at the scrollable div, so imperative scrolling uses the
 * native DOM APIs (`ref.current.scrollTo({ top })`, `scrollTop`,
 * `scrollIntoView`).
 *
 * The masks fade to `--scroll-mask-background` (defaults to
 * `--theme-background`); override it when the ScrollView sits on a different
 * background color.
 */

export interface ScrollViewProps extends ViewProps {
  horizontal?: boolean;
  showScrollIndicators?: boolean;
}

export const ScrollView = forwardRef<HTMLDivElement, ScrollViewProps>(function ScrollViewWithRef(
  { children, className, horizontal = false, showScrollIndicators = false, ...rest },
  ref,
) {
  const axis = horizontal ? 'horizontal' : 'vertical';
  return (
    <View
      ref={ref}
      row={horizontal}
      data-axis={axis}
      className={[styles.scrollView, className]}
      {...rest}
    >
      {showScrollIndicators && <View className={[styles.scrollMask, styles.scrollMaskStart]} />}
      {children}
      {showScrollIndicators && <View className={[styles.scrollMask, styles.scrollMaskEnd]} />}
    </View>
  );
});
